import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Container,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconPencil, IconPlus } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'mantine-datatable';
import dayjs from 'dayjs';
import HttpClient from '../helpers/HttpClient';
import { Message, MessageRecipientOptions } from '../types/message.type';
import { useUserContext } from '../providers/UserContext';
import { USER_RIGHTS } from '../constants/userRights';

function MessageList() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [filtered, setFiltered] = useState<Message[]>([]);
  const [options, setOptions] = useState<MessageRecipientOptions>({ persons: [], tags: [] });

  const [filterPersonIds, setFilterPersonIds] = useState<string[]>([]);
  const [filterTagIds, setFilterTagIds] = useState<string[]>([]);
  const [filterFrom, setFilterFrom] = useState<string | null>(null);
  const [filterTo, setFilterTo] = useState<string | null>(null);

  const [bodyModal, setBodyModal] = useState<Message | null>(null);

  const { userRights } = useUserContext();
  const isWriter = userRights.includes(USER_RIGHTS.MESSAGES);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoadError(null);
      try {
        const [msgResult, recipientOptions] = await Promise.all([
          new HttpClient<Message>('/message').getAll(),
          isWriter
            ? new HttpClient<MessageRecipientOptions>('/message/recipients').getSingle()
            : Promise.resolve(null),
        ]);
        setMessages(msgResult);
        if (recipientOptions) setOptions(recipientOptions);
      } catch {
        setLoadError('Nachrichten konnten nicht geladen werden. Bitte Seite neu laden.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isWriter]);

  useEffect(() => {
    let result = messages;
    // Keep messages whose validity period overlaps with the selected filter range
    if (filterFrom) {
      const from = new Date(filterFrom);
      result = result.filter((m) => !m.validTo || new Date(m.validTo) >= from);
    }
    if (filterTo) {
      const to = new Date(filterTo);
      result = result.filter((m) => !m.validFrom || new Date(m.validFrom) <= to);
    }
    if (filterPersonIds.length > 0) {
      const ids = filterPersonIds.map(Number);
      result = result.filter((m) =>
        (m.recipientPersons ?? []).some((p) => ids.includes(p.id)),
      );
    }
    if (filterTagIds.length > 0) {
      const ids = filterTagIds.map(Number);
      result = result.filter((m) =>
        (m.recipientTags ?? []).some((t) => ids.includes(t.id)),
      );
    }
    setFiltered(result);
  }, [messages, filterFrom, filterTo, filterPersonIds, filterTagIds]);

  const personOptions = options.persons.map((p) => ({
    value: String(p.id),
    label: `${p.lastName}, ${p.firstName}`,
  }));
  const tagOptions = options.tags.map((t) => ({ value: String(t.id), label: t.name }));

  return (
    <Container fluid pb={10} mt={10}>
      <Group gap={25} mb="md">
        <Title order={2}>Nachrichten</Title>
        {isWriter && (
          <ActionIcon
            variant="filled"
            size="xl"
            radius="xl"
            onClick={() => navigate('/messages/create')}
          >
            <IconPlus style={{ width: '70%', height: '70%' }} stroke={1.5} />
          </ActionIcon>
        )}
      </Group>

      {isWriter && (
        <Stack mb="md" gap="sm">
          <Group grow>
            <DateInput
              label="Gültig ab (Filter)"
              clearable
              valueFormat="DD.MM.YYYY"
              value={filterFrom}
              onChange={setFilterFrom}
            />
            <DateInput
              label="Gültig bis (Filter)"
              clearable
              valueFormat="DD.MM.YYYY"
              value={filterTo}
              onChange={setFilterTo}
            />
          </Group>
          <Group grow>
            <MultiSelect
              label="Filter Empfänger (Personen)"
              data={personOptions}
              value={filterPersonIds}
              onChange={setFilterPersonIds}
              searchable
              clearable
            />
            <MultiSelect
              label="Filter Empfänger (Tags)"
              data={tagOptions}
              value={filterTagIds}
              onChange={setFilterTagIds}
              searchable
              clearable
            />
          </Group>
        </Stack>
      )}

      {loadError && <Alert color="red" title="Fehler" mb="md">{loadError}</Alert>}

      {loading ? (
        <Loader size="md" />
      ) : (
        <DataTable
          withTableBorder
          highlightOnHover
          records={filtered}
          onRowClick={({ record }) => setBodyModal(record)}
          columns={[
            {
              accessor: 'subject',
              title: 'Betreff',
              sortable: true,
            },
            {
              accessor: 'sender',
              title: 'Absender',
              render: ({ sender }) =>
                sender ? `${sender.lastName}, ${sender.firstName}` : '—',
            },
            {
              accessor: 'validFrom',
              title: 'Gültig ab',
              render: ({ validFrom }) =>
                validFrom ? dayjs(validFrom).format('DD.MM.YYYY') : '—',
            },
            {
              accessor: 'validTo',
              title: 'Gültig bis',
              render: ({ validTo }) =>
                validTo ? dayjs(validTo).format('DD.MM.YYYY') : '—',
            },
            {
              accessor: 'recipientTags',
              title: 'Tags',
              render: ({ recipientTags }) =>
                (recipientTags ?? []).length > 0 ? (
                  <Group gap={4} wrap="wrap">
                    {(recipientTags ?? []).map((t) => (
                      <Badge key={t.id} color={t.color ?? 'blue'} variant="filled">
                        {t.name}
                      </Badge>
                    ))}
                  </Group>
                ) : (
                  '—'
                ),
            },
            ...(isWriter
              ? [
                  {
                    accessor: 'actions',
                    title: '',
                    textAlign: 'center' as const,
                    width: '3%',
                    render: ({ id }: Message) => (
                      <ActionIcon
                        variant="light"
                        color="blue"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/messages/edit/${id}`);
                        }}
                      >
                        <IconPencil size={18} />
                      </ActionIcon>
                    ),
                  },
                ]
              : []),
          ]}
        />
      )}

      <Modal
        opened={bodyModal !== null}
        onClose={() => setBodyModal(null)}
        title={bodyModal?.subject ?? ''}
        size="lg"
      >
        {bodyModal && (
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              Von: {bodyModal.sender ? `${bodyModal.sender.lastName}, ${bodyModal.sender.firstName}` : '—'}
              {bodyModal.validFrom && ` | ab ${dayjs(bodyModal.validFrom).format('DD.MM.YYYY')}`}
              {bodyModal.validTo && ` | bis ${dayjs(bodyModal.validTo).format('DD.MM.YYYY')}`}
            </Text>
            {/* Internal business app — HTML from trusted writers */}
            <div dangerouslySetInnerHTML={{ __html: bodyModal.body }} />
          </Stack>
        )}
      </Modal>
    </Container>
  );
}

export default MessageList;
